import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import SectionTitle from '@/components/atoms/fonts/SectionTitle';
import ButtonCallAction from '@/components/BI/buttonCallAction';
import CreateSimulatorHistoryModal from '@/components/molecules/modals/CreateSimulatorHistoryModal';
import LoadingComponent from '@/components/templates/LoadingComponent';
import useSnackbar from '@/hooks/useSnackbar';
import useSimulatorsService from '@/services-simuladores/simulators';
import { ReactComponent as PDFIcon } from '@assets/icons/PDFIcon.svg';
import { ReactComponent as SaveIcon } from '@assets/icons/SaveIcon.svg';

import { OutputsRenderer } from './components/OutputsRenderer';
import { Container, ButtonAdd } from './styles';

export const SimulatorsOutputs = ({ property }) => {
    const location = useLocation();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const params = useParams();
    const { generateSimulatorPDF, createSimulatorHistory, getSimulatorHistoryById } = useSimulatorsService();
    const [simulatorData, setSimulatorData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingButton, setLoadingButton] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [title, setTitle] = useState('');

    const user = useSelector((state) => state.auth.user);
    const segment = useSelector((state) => state.auth.currentSegment);
    const segmentName = segment === 2 ? 'Rehsult Grãos' : 'Rehsult Leite';

    const isProdutor = Number(sessionStorage.getItem('currentUserRole')) === 8;
    const isViewingAsConsultant = sessionStorage.getItem('consultant_id') !== null;

    useEffect(() => {
        const load = async () => {
            // Se veio do cálculo direto (sem id), usa o estado
            if (location.state?.calculateSimulator) {
                setSimulatorData(location.state.calculateSimulator.data);
                setIsLoading(false);
            }
            // Se veio da rota de histórico (tem entityId), busca via API
            else if (params.entityId) {
                try {
                    const resp = await getSimulatorHistoryById(params.entityId);
                    setSimulatorData(resp.data);
                } catch (err) {
                    snackbar('Erro ao carregar simulação', 'error');
                } finally {
                    setIsLoading(false);
                }
            } else {
                snackbar('Simulação inválida ou ausente.', 'error');
                setIsLoading(false);
            }
        };

        load();
    }, [params.entityId]);

    const handleGeneratePdf = async () => {
        setLoadingButton(true);
        try {
            const pdfBlob = await generateSimulatorPDF(
                simulatorData.simulator?.name || simulatorData.title,
                simulatorData.outputs?.boxes || simulatorData.data?.boxes
            );

            const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `resultado_simulador-${simulatorData.simulator?.name || simulatorData?.title}.pdf`);
            document.body.appendChild(link);
            link.click();
        } catch (err) {
            console.error('Erro ao gerar PDF:', err);
        }
        setLoadingButton(false);
    };

    const handleCreateSimulatorHistory = async () => {
        if (!title) return snackbar('Preencha o título', 'error');

        try {
            await createSimulatorHistory({
                data: simulatorData.outputs,
                simulator_id: simulatorData.simulator?.id,
                property_id: params.propId,
                enterprise_id: user.enterprise.id,
                origin: segmentName,
                user_id: user.id,
                title
            });
            snackbar('Simulação salva com sucesso', 'success');
            navigate(`/consultor/propriedades/${params.propId}/simulador_economicoV2/${simulatorData.simulator?.id}`);
        } catch (err) {
            snackbar('Erro ao salvar simulação', 'error');
        }
    };

    const handleNewSimulation = () => {
        navigate(`/consultor/propriedades/${params.propId}/nova_simulacao/${simulatorData.simulator?.id}`, {
            state: { previousValues: [] }
        });
    };

    if (isLoading || !simulatorData) {
        return <LoadingComponent />;
    }

    return (
        <>
            <SectionTitle tituloString={`Resultado - ${simulatorData.simulator?.name || simulatorData.title}`} />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Container>
                        <OutputsRenderer boxes={simulatorData.outputs?.boxes || simulatorData.data?.boxes} />
                    </Container>
                </Grid>
            </Grid>

            <Grid container justifyContent="center" mt={3} mb={3} spacing={2} display="flex" gap={2}>
                {!isProdutor && !isViewingAsConsultant && (
                    <ButtonAdd variant="outlined" size="small" onClick={handleNewSimulation} width="190px" height="44px">
                        Nova Simulação
                    </ButtonAdd>
                )}

                {!isProdutor && !isViewingAsConsultant && simulatorData.simulator?.id && (
                    <ButtonCallAction
                        width="190px"
                        variant="contained"
                        size="small"
                        onClick={() => setOpenModal(true)}
                        loading={loadingButton}
                        disabled={loadingButton}
                        Icon={SaveIcon}
                        iconSize={24}
                        sx={{ textTransform: 'none', width: '190px', height: '44px', fontWeight: 600 }}
                    >
                        Salvar
                    </ButtonCallAction>
                )}

                <ButtonCallAction
                    width="190px"
                    variant="contained"
                    size="small"
                    onClick={handleGeneratePdf}
                    loading={loadingButton}
                    disabled={loadingButton}
                    Icon={PDFIcon}
                    iconSize={24}
                    sx={{ textTransform: 'none', width: '190px', height: '44px', fontWeight: 600 }}
                >
                    Gerar PDF
                </ButtonCallAction>
            </Grid>

            {!isProdutor && (
                <CreateSimulatorHistoryModal
                    open={openModal}
                    handleClose={() => setOpenModal(false)}
                    title={title}
                    setTitle={setTitle}
                    handleCreateSimulatorHistory={handleCreateSimulatorHistory}
                />
            )}
        </>
    );
};

SimulatorsOutputs.propTypes = {
    property: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    }).isRequired
};
